/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { 
  Award, ShieldCheck, Phone, Mail, MapPin, LogIn, LogOut, UserPlus, 
  BookOpen, ChevronRight, CheckCircle2, ChevronDown, User, Sparkles, 
  Check, RefreshCw, X, ShieldAlert, FileText, ArrowRight, BookMarked, Menu,
  Clock
} from 'lucide-react';
import RichContentRenderer from './components/RichContentRenderer';
import { useAuthSession } from './hooks/useAuthSession';
import { User as UserType, Test, CMSContent, LegalPage } from './types';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { 
  getPublicCMSDirect, 
  getPublicInstitutesDirect, 
  getPublicLegalPagesDirect,
  directSupabaseLogin,
  directSupabaseRegister 
} from './lib/supabase';
import { dataService } from './lib/dataService';
import { safeApiFetch, apiClient } from './utils/apiClient';

const NoticeBoard = lazy(() => import('./components/NoticeBoard'));
const Blogs = lazy(() => import('./components/Blogs'));
const ExamConsole = lazy(() => import('./components/ExamConsole'));
const Dashboards = lazy(() => import('./components/Dashboards'));
const CompetitionEngine = lazy(() => import('./components/CompetitionEngine'));

export default function App() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<UserType | null>(null);
  const [view, setView] = useState<'home' | 'blogs' | 'dashboard' | 'exam' | 'competition'>('home');
  const [activeTest, setActiveTest] = useState<Test | null>(null);
  const [activeAttemptId, setActiveAttemptId] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Auth Overlay Modal States
  const [authModal, setAuthModal] = useState<'login' | 'signup' | null>(null);
  const [loginRole, setLoginRole] = useState<'student' | 'institute'>('student');
  const [footerModal, setFooterModal] = useState<string | null>(null);
  const [institutes, setInstitutes] = useState<any[]>([]);
  const [cms, setCms] = useState<CMSContent | null>(null);
  const [legalPages, setLegalPages] = useState<LegalPage[]>([]);
  const [isCmsLoading, setIsCmsLoading] = useState<boolean>(true);
  const [isTestsLoading, setIsTestsLoading] = useState<boolean>(true);

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedInstId, setSelectedInstId] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP Verification States
  const [otpVerificationEmail, setOtpVerificationEmail] = useState<string | null>(null);
  const [otpCodeInput, setOtpCodeInput] = useState('');
  const [otpDemoHint, setOtpDemoHint] = useState<string | null>(null);

  // Force Password Change States
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmAdminPassword, setConfirmAdminPassword] = useState('');
  const [forcePassError, setForcePassError] = useState('');
  const [forcePassLoading, setForcePassLoading] = useState(false);

  // Score modal after completing test
  const [completedReport, setCompletedReport] = useState<any | null>(null);

  // Newsletter Subscription States
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [newsletterMsg, setNewsletterMsg] = useState('');

  // Active Exam Countdown States
  const [examTimeLeft, setExamTimeLeft] = useState<number | null>(null);
  const [submitExamTrigger, setSubmitExamTrigger] = useState<(() => void) | null>(null);

  const handleForcePasswordChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForcePassError('');

    if (newAdminPassword.length < 8) {
      setForcePassError('Password must be at least 8 characters long.');
      return;
    }
    if (newAdminPassword !== confirmAdminPassword) {
      setForcePassError('Passwords do not match.');
      return;
    }

    setForcePassLoading(true);
    const result = await safeApiFetch('/api/auth/change-password-force', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ newPassword: newAdminPassword })
    });

    if (!result.ok) {
      setForcePassError(result.error || 'Failed to update password.');
    } else {
      alert('Password changed successfully. Your super admin session is now active!');
      if (user) {
        setUser({
          ...user,
          mustChangePassword: false
        } as any);
      }
      setNewAdminPassword('');
      setConfirmAdminPassword('');
    }
    setForcePassLoading(false);
  };

  // Synchronize Auth Profile Session
  const syncProfile = async (authToken: string) => {
    const result = await safeApiFetch('/api/auth/me', {
      headers: { 'Authorization': `Bearer ${authToken}` }
    });
    if (result.ok && result.data) {
      setUser(result.data);
    } else if (result.status === 401 || result.status === 403) {
      // Bad session token, wipe clean
      handleLogout();
    }
  };

  // Call persistent Super Admin / Student session verification hook
  useAuthSession(token, setUser, setView, syncProfile);

  // Fetch Public Metadata (Institutes list, CMS config, Legal pages directly from Supabase via dataService)
  const fetchPublicMetadata = async () => {
    setIsCmsLoading(true);
    try {
      const [cmsData, instData, legalData] = await Promise.all([
        dataService.getCMS(),
        dataService.getInstitutes(),
        dataService.getLegalPages()
      ]);

      if (cmsData) setCms(cmsData);
      if (instData) setInstitutes(instData);
      if (legalData) setLegalPages(legalData);
    } catch (err) {
      console.error('Error fetching public asset metadata via Supabase client:', err);
    } finally {
      setIsCmsLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicMetadata();
    
    // Check if referral code is in URL search params (e.g. ?ref=ANJALI66)
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    if (refParam) {
      setReferralCode(refParam.toUpperCase());
      setAuthModal('signup'); // Prompt signup with referral auto-loaded!
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setView('home');
    setActiveTest(null);
    setActiveAttemptId(null);
    setCompletedReport(null);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim() || !newsletterEmail.includes('@')) {
      setNewsletterStatus('error');
      setNewsletterMsg('Please enter a valid email address.');
      return;
    }

    setNewsletterStatus('loading');
    setNewsletterMsg('');

    const result = await safeApiFetch('/api/newsletter/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newsletterEmail })
    });

    if (result.ok) {
      setNewsletterStatus('success');
      setNewsletterMsg(result.data?.message || 'Successfully subscribed! Check your inbox soon.');
      setNewsletterEmail('');
      setTimeout(() => {
        setNewsletterStatus('idle');
        setNewsletterMsg('');
      }, 5000);
    } else {
      setNewsletterStatus('error');
      setNewsletterMsg(result.error || 'Subscription failed. Please try again.');
    }
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    const isLogin = authModal === 'login';
    const url = isLogin 
      ? (loginRole === 'institute' ? '/api/auth/institute/login' : '/api/auth/login') 
      : '/api/auth/register';

    const cleanEmail = (email || '').trim();
    const cleanPassword = password || '';
    const cleanName = (name || '').trim();
    const cleanPhone = (phone || '').trim();
    const cleanReferral = (referralCode || '').trim();

    const payload = isLogin 
      ? { email: cleanEmail, password: cleanPassword } 
      : { 
          email: cleanEmail, 
          password: cleanPassword, 
          name: cleanName, 
          phone: cleanPhone, 
          instituteId: selectedInstId || null, 
          referralCode: cleanReferral || null 
        };

    const result = await safeApiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!result.ok) {
      if (result.data && result.data.error === 'otp_verification_pending') {
        setOtpVerificationEmail(cleanEmail);
        setOtpDemoHint(result.data.otpCode || null);
        setAuthModal(null);
      } else if (isLogin) {
        // Fallback to direct Supabase authentication for static hosting (e.g. Firebase Hosting)
        const sbResult = await directSupabaseLogin(cleanEmail, cleanPassword, loginRole);
        if (sbResult.ok && sbResult.data) {
          localStorage.setItem('token', sbResult.data.token);
          localStorage.setItem('user', JSON.stringify(sbResult.data.user));
          setToken(sbResult.data.token);
          setUser(sbResult.data.user);
          setAuthModal(null);
          setEmail('');
          setPassword('');
          setName('');
          setPhone('');
          setSelectedInstId('');
          setReferralCode('');
          setView('dashboard');
        } else {
          setAuthError(sbResult.error || result.error || 'Authentication check failed');
        }
      } else {
        // Fallback registration for static hosting environments
        const regResult = await directSupabaseRegister(
          cleanEmail,
          cleanPassword,
          cleanName,
          cleanPhone,
          selectedInstId,
          cleanReferral
        );
        if (regResult.ok && regResult.data) {
          localStorage.setItem('token', regResult.data.token);
          localStorage.setItem('user', JSON.stringify(regResult.data.user));
          setToken(regResult.data.token);
          setUser(regResult.data.user);
          setAuthModal(null);
          setEmail('');
          setPassword('');
          setName('');
          setPhone('');
          setSelectedInstId('');
          setReferralCode('');
          setView('dashboard');
        } else {
          setAuthError(regResult.error || result.error || 'Registration failed');
        }
      }
    } else {
      if (!isLogin) {
        setOtpVerificationEmail(cleanEmail);
        setOtpDemoHint(result.data?.otpCode || null);
        setAuthModal(null);
        setPassword('');
        setName('');
        setPhone('');
        setSelectedInstId('');
        setReferralCode('');
      } else {
        localStorage.setItem('token', result.data.token);
        localStorage.setItem('user', JSON.stringify(result.data.user));
        setToken(result.data.token);
        setUser(result.data.user);
        setAuthModal(null);
        setEmail('');
        setPassword('');
        setName('');
        setPhone('');
        setSelectedInstId('');
        setReferralCode('');
        setView('dashboard');
      }
    }
    setLoading(false);
  };

  const handleOtpVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    const cleanEmail = (otpVerificationEmail || '').trim();
    const cleanOtp = (otpCodeInput || '').trim();

    const result = await safeApiFetch('/api/auth/verify-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, otpCode: cleanOtp })
    });

    if (!result.ok) {
      setAuthError(result.error || 'Verification failed. Please check the OTP code.');
    } else {
      localStorage.setItem('token', result.data.token);
      setToken(result.data.token);
      setUser(result.data.user);
      setOtpVerificationEmail(null);
      setOtpDemoHint(null);
      setOtpCodeInput('');
      setEmail('');
      setView('dashboard');
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setAuthError('');
    setLoading(true);

    const cleanEmail = (otpVerificationEmail || '').trim();
    const result = await safeApiFetch('/api/auth/resend-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail })
    });

    if (result.ok) {
      setOtpDemoHint(result.data?.otpCode);
      alert('A new 6-digit OTP code has been generated and sent!');
    } else {
      setAuthError(result.error || 'Resend failed');
    }
    setLoading(false);
  };

  // Student initiates a mock exam
  const handleBeginMockTest = async (testObj: Test) => {
    if (!token) {
      setAuthModal('login');
      return;
    }

    setLoading(true);
    const result = await safeApiFetch('/api/student/attempts/start', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ testId: testObj.id })
    });

    if (result.ok && result.data) {
      setActiveAttemptId(result.data.id);
      setActiveTest(testObj);
      setView('exam');
    } else {
      alert(result.error || 'Failed to start exam. Make sure your subscription is active if required.');
    }
    setLoading(false);
  };

  // Receive completed report from Exam Console
  const handleExamCompleted = (report: any) => {
    setCompletedReport(report);
    setView('dashboard');
    setActiveTest(null);
    setActiveAttemptId(null);
  };

  // List of tests fetched from database via dataService
  const [allTestsList, setAllTestsList] = useState<any[]>([]);
  
  const fetchAllTests = async () => {
    setIsTestsLoading(true);
    try {
      const tests = await dataService.getTests();
      setAllTestsList(tests || []);
    } catch (err) {
      console.error('Error fetching tests:', err);
      setAllTestsList([]);
    } finally {
      setIsTestsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTests();
  }, [token]);

  // Dynamic SEO Metadata for Deep Indexing
  const getSeoData = () => {
    switch (view) {
      case 'competition':
        return {
          title: cms?.seoCompetitionTitle || 'Student Blog Strategy Competition 2026 | NextGen लक्ष्य',
          desc: cms?.seoCompetitionDesc || 'Join India\'s premier student-written blog strategy competition. Read high-level prep tactics, learning hacks, and strategy papers by fellow CAT, JEE, NEET, and Banking candidates, and support them by voting.',
          image: cms?.seoCompetitionOgImage || 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80'
        };
      case 'blogs':
        return {
          title: cms?.seoBlogsTitle || 'Prep Blogs | NextGen लक्ष्य',
          desc: cms?.seoBlogsDesc || 'Read high-level prep tactics, learning hacks, and strategy papers by fellow candidates.',
          image: cms?.seoBlogsOgImage || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=630&fit=crop&q=80'
        };
      case 'dashboard':
        return {
          title: cms?.seoPlansTitle || 'Student Dashboard & Plans | NextGen लक्ष्य',
          desc: cms?.seoPlansDesc || 'Explore subscription plans, mock tests, dynamic analytics, and detailed diagnostic dashboards.',
          image: cms?.seoPlansOgImage || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=630&fit=crop&q=80'
        };
      case 'home':
      default:
        return {
          title: cms?.seoHomeTitle || 'NextGen लक्ष्य 2026 | Realistic CAT, JEE, NEET, Banking Mock Exams',
          desc: cms?.seoHomeDesc || 'Crack CAT, JEE, NEET, and Banking exams with realistic mock test engines, robust diagnostic dashboards, and certified batch tracking.',
          image: cms?.seoHomeOgImage || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=630&fit=crop&q=80'
        };
    }
  };

  const seoData = getSeoData();
  const metaTitle = seoData.title;
  const metaDesc = seoData.desc;
  const metaImage = seoData.image;

  const currentUrl = view === 'competition'
    ? 'https://ais-pre-wicdc6ieppkfcaatkz4vvw-925028468792.asia-east1.run.app/?view=competition'
    : 'https://ais-pre-wicdc6ieppkfcaatkz4vvw-925028468792.asia-east1.run.app/';

  return (
    <HelmetProvider>
      <div id="nextgen-lakshya-app" className="min-h-screen bg-slate-950 text-slate-100 font-sans flex flex-col justify-between overflow-x-hidden">
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDesc} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={currentUrl} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDesc} />
          <meta property="og:image" content={metaImage} />
          
          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={currentUrl} />
          <meta property="twitter:title" content={metaTitle} />
          <meta property="twitter:description" content={metaDesc} />
          <meta property="twitter:image" content={metaImage} />

          {/* Deep Indexing tag */}
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        </Helmet>
      
      {/* 1. SCROLLING NOTICE BOARD MARQUEE AT TOP */}
      {view !== 'exam' && (
        <Suspense fallback={null}>
          <NoticeBoard onRefreshTrigger={authModal === null} />
        </Suspense>
      )}

      {/* 2. DYNAMIC HEADER AND NAV BAR */}
      <header id="nextgen-nav-header" className="sticky top-0 bg-slate-950/90 backdrop-blur-md border-b border-slate-900/60 z-50 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => { if (view !== 'exam') { setView('home'); setIsMobileMenuOpen(false); } }}>
            <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white font-sans font-black text-sm px-2.5 py-1 rounded-xl shadow-lg border border-amber-400">
              लक्ष्य
            </span>
            <div>
              <h1 className="text-sm font-black text-slate-100 tracking-tight leading-none uppercase">NextGen लक्ष्य</h1>
              <p className="text-[9px] text-amber-500 font-mono uppercase tracking-widest mt-0.5 leading-none font-bold">
                {view === 'exam' ? 'Active Exam Console' : 'Exam Console'}
              </p>
            </div>
          </div>

          {view === 'exam' ? (
            /* Live Countdown Timer inside Navigation Header specifically during Exam */
            <div className="flex items-center gap-4 flex-1 justify-end">
              <div className="hidden sm:block text-right max-w-xs truncate">
                <span className="text-[10px] text-slate-400 block font-mono font-bold uppercase tracking-wider">Exam Title</span>
                <span className="text-xs text-slate-200 font-extrabold truncate block">{activeTest?.title}</span>
              </div>
              <div className="bg-slate-900/90 border border-amber-500/20 rounded-xl py-1.5 px-3 flex items-center gap-2 shadow-inner">
                {/* Circular Progress surrounding Clock Icon */}
                <div className="relative w-8 h-8 flex items-center justify-center">
                  <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                    <circle
                      cx="16"
                      cy="16"
                      r="13"
                      className="stroke-slate-800"
                      strokeWidth="2.5"
                      fill="transparent"
                    />
                    <circle
                      cx="16"
                      cy="16"
                      r="13"
                      className={`transition-all duration-1000 ease-linear ${(examTimeLeft || 0) < 120 ? 'stroke-rose-500 animate-pulse' : 'stroke-emerald-500'}`}
                      strokeWidth="2.5"
                      fill="transparent"
                      strokeDasharray={2 * Math.PI * 13}
                      strokeDashoffset={2 * Math.PI * 13 * (1 - ((examTimeLeft || 0) / ((activeTest?.durationMinutes || 60) * 60)))}
                      strokeLinecap="round"
                    />
                  </svg>
                  <Clock className={`w-3.5 h-3.5 relative z-10 ${(examTimeLeft || 0) < 120 ? 'text-rose-400 animate-ping' : 'text-amber-400'}`} />
                </div>
                <div className="text-left">
                  <p className="text-[9px] text-slate-400 font-mono tracking-wider uppercase leading-none">Time Remaining</p>
                  <p id="nav-exam-timer" className={`text-xs font-mono font-bold tracking-wider leading-none mt-1 ${(examTimeLeft || 0) < 120 ? 'text-rose-400 animate-pulse' : 'text-emerald-400'}`}>
                    {examTimeLeft !== null ? (() => {
                      const hrs = Math.floor(examTimeLeft / 3600);
                      const mins = Math.floor((examTimeLeft % 3600) / 60);
                      const secs = examTimeLeft % 60;
                      return `${hrs > 0 ? hrs + ':' : ''}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                    })() : 'Loading...'}
                  </p>
                </div>
              </div>
              {submitExamTrigger && (
                <button
                  onClick={submitExamTrigger}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold py-2 px-3 sm:px-4 rounded-xl transition-all shadow-md hover:shadow-emerald-950/20 border border-emerald-500 focus:outline-none"
                >
                  Submit Exam
                </button>
              )}
            </div>
          ) : (
            <>
              <nav className="hidden md:flex items-center gap-6 text-xs font-semibold text-slate-400">
                <button onClick={() => setView('home')} className={`hover:text-amber-400 transition-colors ${view === 'home' ? 'text-amber-400' : ''}`}>Home Dashboard</button>
                <button onClick={() => setView('blogs')} className={`hover:text-amber-400 transition-colors ${view === 'blogs' ? 'text-amber-400' : ''}`}>Prep Blogs</button>
                <button onClick={() => setView('competition')} className={`hover:text-amber-400 transition-colors ${view === 'competition' ? 'text-amber-400' : ''}`}>Competition</button>
                {token && (
                  <button onClick={() => setView('dashboard')} className={`hover:text-amber-400 transition-colors ${view === 'dashboard' ? 'text-amber-400' : ''}`}>Dashboard</button>
                )}
              </nav>

              <div className="hidden md:flex items-center gap-3">
                {token && user ? (
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setView('dashboard')}
                      className="bg-slate-900 border border-slate-800 text-slate-200 hover:text-white text-xs font-semibold py-2 px-4 rounded-xl shadow-sm transition-all flex items-center gap-1.5 focus:outline-none"
                    >
                      <User className="w-3.5 h-3.5 text-amber-400" />
                      <span>Portal Dashboard</span>
                    </button>
                    <button 
                      onClick={handleLogout}
                      className="p-2 border border-rose-500/20 bg-rose-500/5 text-rose-400 hover:bg-rose-500/10 rounded-xl transition-colors focus:outline-none"
                      title="Log Out Session"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setAuthModal('login')}
                      className="text-slate-400 hover:text-slate-100 text-xs font-bold py-2 px-4 focus:outline-none"
                    >
                      Log In
                    </button>
                    <button 
                      onClick={() => setAuthModal('signup')}
                      className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-sans font-extrabold text-xs py-2 px-4 rounded-xl shadow-lg shadow-amber-500/10 border border-amber-400/20 transition-all focus:outline-none"
                    >
                      Register Account
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Hamburger Menu Button */}
              <div className="flex md:hidden items-center">
                <button
                  id="mobile-menu-hamburger-btn"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="flex items-center justify-center p-2 rounded-xl border border-slate-800 bg-slate-900 text-slate-300 hover:text-amber-400 focus:outline-none min-h-[44px] min-w-[44px]"
                  aria-label="Toggle Navigation Menu"
                >
                  {isMobileMenuOpen ? <X className="w-5.5 h-5.5 text-amber-400" /> : <Menu className="w-5.5 h-5.5" />}
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* 2b. MOBILE SLIDE-OUT DRAWER OVERLAY */}
      {view !== 'exam' && isMobileMenuOpen && (
        <div id="mobile-nav-drawer" className="md:hidden fixed inset-x-0 top-[65px] bg-slate-950/98 backdrop-blur-xl border-b border-slate-900 z-50 animate-fade-in py-4 px-6 space-y-4 shadow-2xl max-h-[calc(100vh-65px)] overflow-y-auto">
          <nav className="flex flex-col space-y-2">
            <button 
              onClick={() => { setView('home'); setIsMobileMenuOpen(false); }} 
              className={`w-full text-left py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2.5 min-h-[44px] ${view === 'home' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'text-slate-400 hover:text-white'}`}
            >
              <span>Home Dashboard</span>
            </button>
            <button 
              onClick={() => { setView('blogs'); setIsMobileMenuOpen(false); }} 
              className={`w-full text-left py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2.5 min-h-[44px] ${view === 'blogs' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'text-slate-400 hover:text-white'}`}
            >
              <span>Prep Blogs</span>
            </button>
            <button 
              onClick={() => { setView('competition'); setIsMobileMenuOpen(false); }} 
              className={`w-full text-left py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2.5 min-h-[44px] ${view === 'competition' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'text-slate-400 hover:text-white'}`}
            >
              <span>Strategy Competition</span>
            </button>
            {token && (
              <button 
                onClick={() => { setView('dashboard'); setIsMobileMenuOpen(false); }} 
                className={`w-full text-left py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2.5 min-h-[44px] ${view === 'dashboard' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'text-slate-400 hover:text-white'}`}
              >
                <span>Portal Dashboard</span>
              </button>
            )}
          </nav>

          <div className="pt-4 border-t border-slate-900 flex flex-col gap-2">
            {token && user ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2.5 px-4 py-2.5 bg-slate-900/60 rounded-xl border border-slate-850/60">
                  <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-xs">
                    {user.name.substring(0, 2).toUpperCase()}
                  </div>
                  <span className="text-xs text-slate-300 font-bold truncate max-w-[200px]">{user.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => { setView('dashboard'); setIsMobileMenuOpen(false); }}
                    className="w-full text-center bg-slate-900 border border-slate-800 text-slate-200 hover:text-white text-xs font-bold py-3 px-4 rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 min-h-[44px] focus:outline-none"
                  >
                    <User className="w-3.5 h-3.5 text-amber-400" />
                    <span>Portal</span>
                  </button>
                  <button 
                    onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    className="w-full text-center bg-rose-500/5 hover:bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold py-3 px-4 rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 min-h-[44px] focus:outline-none"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3.5">
                <button 
                  onClick={() => { setAuthModal('login'); setIsMobileMenuOpen(false); }}
                  className="w-full text-center text-slate-300 hover:text-slate-100 bg-slate-900 border border-slate-800 text-xs font-bold py-3 px-4 rounded-xl min-h-[44px] focus:outline-none"
                >
                  Log In
                </button>
                <button 
                  onClick={() => { setAuthModal('signup'); setIsMobileMenuOpen(false); }}
                  className="w-full text-center bg-amber-500 hover:bg-amber-600 text-slate-950 font-sans font-extrabold text-xs py-3 px-4 rounded-xl shadow-lg border border-amber-400/20 min-h-[44px] focus:outline-none"
                >
                  Register
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 3. CORE VIEW COMPONENT DISPATCH */}
      <div className="flex-1">
        <Suspense fallback={
          <div className="flex items-center justify-center py-20 text-slate-400">
            <RefreshCw className="w-7 h-7 animate-spin text-amber-500 mr-3" />
            <span className="font-medium text-sm">Loading module...</span>
          </div>
        }>
          {view === 'exam' && activeTest && activeAttemptId && token && (
            <ExamConsole 
              token={token} 
              test={activeTest} 
              attemptId={activeAttemptId} 
              onFinish={handleExamCompleted}
              onExit={() => {
                setView('dashboard');
                setExamTimeLeft(null);
                setSubmitExamTrigger(null);
              }}
              onTimeUpdate={(seconds: number) => setExamTimeLeft(seconds)}
              setSubmitExamTrigger={setSubmitExamTrigger}
            />
          )}

          {view === 'dashboard' && token && user && (
            <Dashboards 
              token={token} 
              user={user} 
              onLogout={handleLogout}
              onSelectTest={handleBeginMockTest}
              tests={allTestsList}
            />
          )}

          {view === 'blogs' && (
            <Blogs 
              token={token} 
              onLoginPrompt={() => setAuthModal('login')} 
              currentUserId={user?.id}
            />
          )}

          {view === 'competition' && (
            <CompetitionEngine
              token={token}
              onLoginPrompt={() => setAuthModal('login')}
              currentUserId={user?.id}
              cms={cms}
            />
          )}
        </Suspense>

        {view === 'home' && (
          // PUBLIC HOMEPAGE LANDING WITH HERO, CAROUSEL, STATS & BLOG SECTION
          <div id="homepage-root" className="space-y-16 animate-fade-in">
            
            {/* HERO LANDING JUMBOTRON */}
            <section id="hero-landing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-8 text-center space-y-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold tracking-wider uppercase font-mono mb-4 animate-pulse">
                <Sparkles className="w-3.5 h-3.5" />
                <span>NextGen लक्ष्य 2026</span>
              </div>

              <h1 id="cms-hero-title" className="text-3.5xl sm:text-5.5xl font-sans font-black text-slate-100 tracking-tight max-w-4xl mx-auto leading-tight">
                {cms?.heroTitle || 'Crack Your Dream Competitive Exam With NextGen लक्ष्य'}
              </h1>

              <p id="cms-hero-subtitle" className="text-slate-400 text-sm sm:text-md max-w-2xl mx-auto leading-relaxed font-sans">
                {cms?.heroSubtitle || 'India\'s most realistic CAT, JEE, NEET, and Banking mock exam engine. Complete with real-time test consoles, detailed performance diagnostics, and certified batch tracking.'}
              </p>

              <div className="flex items-center justify-center gap-3 pt-4">
                <button 
                  onClick={() => token ? setView('dashboard') : setAuthModal('signup')}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-sans font-black text-xs sm:text-sm py-3 px-6 rounded-xl shadow-xl shadow-amber-500/10 hover:shadow-amber-500/15 border border-amber-400/20 transition-all flex items-center gap-1.5 focus:outline-none uppercase tracking-wider"
                >
                  <span>Practice Free Mocks</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setView('blogs')}
                  className="bg-slate-900 border border-slate-800 text-slate-350 hover:text-white text-xs sm:text-sm py-3 px-6 rounded-xl shadow-md transition-all focus:outline-none font-bold flex items-center gap-1.5"
                >
                  <BookMarked className="w-4 h-4 text-slate-400" />
                  <span>Prep Articles</span>
                </button>
              </div>

              {/* BENTO STATS COUNTERS */}
              <div id="bento-stats-grid" className="grid gap-4 grid-cols-2 sm:grid-cols-3 max-w-3xl mx-auto pt-10 text-center font-mono">
                <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-850 p-4 rounded-2xl">
                  <p className="text-slate-400 uppercase tracking-widest text-[10px] font-bold">Active Candidates</p>
                  <p id="cms-stat-students" className="text-2xl font-sans font-black text-slate-100 mt-2">{cms?.statsStudents || '25,000+'}</p>
                </div>
                <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-850 p-4 rounded-2xl">
                  <p className="text-slate-400 uppercase tracking-widest text-[10px] font-bold">Qualified Rate</p>
                  <p id="cms-stat-success" className="text-2xl font-sans font-black text-amber-400 mt-2">{cms?.statsSuccessRate || '98.4%'}</p>
                </div>
                <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-850 p-4 rounded-2xl col-span-2 sm:col-span-1">
                  <p className="text-slate-400 uppercase tracking-widest text-[10px] font-bold">Mock Exams Listed</p>
                  <p id="cms-stat-mocks" className="text-2xl font-sans font-black text-slate-100 mt-2">{cms?.statsMockTests || '450+'}</p>
                </div>
              </div>
            </section>

            {/* SYLLABUS CAROUSEL CATEGORY PREPARATION */}
            <section id="syllabus-carousel-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="text-center max-w-xl mx-auto mb-10">
                <h2 className="text-2xl font-sans font-bold text-slate-100 tracking-tight">Our Competitive Mock Modules</h2>
                <p className="text-xs text-slate-400 mt-1">Select your exam stream and practice inside state-of-the-art CBT exam environments.</p>
              </div>

              <div id="syllabus-grid" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 font-sans">
                {/* CAT Card */}
                <div 
                  onClick={() => setAuthModal('signup')}
                  className="group bg-slate-900/30 hover:bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-amber-500/20 p-6 rounded-2xl transition-all duration-350 cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    <span className="text-[10px] font-mono font-bold tracking-wider uppercase text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/15">CAT Prep</span>
                    <h3 className="text-md font-bold text-slate-150 leading-snug mt-3">CAT / MBA Simulator</h3>
                    <p className="text-xs text-slate-450 leading-relaxed mt-1.5">Includes advanced quantitative aptitude, verbal ability, and complex logical datasets.</p>
                  </div>
                  <div className="text-xs font-bold text-amber-500 mt-6 group-hover:translate-x-1 transition-transform flex items-center gap-0.5">
                    Start Mocks &rarr;
                  </div>
                </div>

                {/* JEE Card */}
                <div 
                  onClick={() => setAuthModal('signup')}
                  className="group bg-slate-900/30 hover:bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-amber-500/20 p-6 rounded-2xl transition-all duration-350 cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    <span className="text-[10px] font-mono font-bold tracking-wider uppercase text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/15">JEE Prep</span>
                    <h3 className="text-md font-bold text-slate-150 leading-snug mt-3">JEE Mains / Advanced</h3>
                    <p className="text-xs text-slate-450 leading-relaxed mt-1.5">Sectional physics formulas, organic mechanisms, coordinate mathematics integrations.</p>
                  </div>
                  <div className="text-xs font-bold text-amber-500 mt-6 group-hover:translate-x-1 transition-transform flex items-center gap-0.5">
                    Start Mocks &rarr;
                  </div>
                </div>

                {/* NEET Card */}
                <div 
                  onClick={() => setAuthModal('signup')}
                  className="group bg-slate-900/30 hover:bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-amber-500/20 p-6 rounded-2xl transition-all duration-350 cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    <span className="text-[10px] font-mono font-bold tracking-wider uppercase text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/15">NEET Prep</span>
                    <h3 className="text-md font-bold text-slate-150 leading-snug mt-3">NEET UG Medical</h3>
                    <p className="text-xs text-slate-450 leading-relaxed mt-1.5">Detailed Botany concepts, Zoology structural items, inorganic organic chemistry mocks.</p>
                  </div>
                  <div className="text-xs font-bold text-amber-500 mt-6 group-hover:translate-x-1 transition-transform flex items-center gap-0.5">
                    Start Mocks &rarr;
                  </div>
                </div>

                {/* BANKING Card */}
                <div 
                  onClick={() => setAuthModal('signup')}
                  className="group bg-slate-900/30 hover:bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-amber-500/20 p-6 rounded-2xl transition-all duration-350 cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    <span className="text-[10px] font-mono font-bold tracking-wider uppercase text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded border border-sky-500/15">Banking Prep</span>
                    <h3 className="text-md font-bold text-slate-150 leading-snug mt-3">SBI & IBPS PO Exams</h3>
                    <p className="text-xs text-slate-450 leading-relaxed mt-1.5">Numerical calculations, structural reasoning logical grids, grammatical guidelines.</p>
                  </div>
                  <div className="text-xs font-bold text-amber-500 mt-6 group-hover:translate-x-1 transition-transform flex items-center gap-0.5">
                    Start Mocks &rarr;
                  </div>
                </div>
              </div>
            </section>

            {/* PUBLIC BLOG INSIGHTS PREVIEW GRID */}
            <section id="homepage-blogs" className="border-t border-slate-900 pt-16">
              <Blogs 
                token={token} 
                onLoginPrompt={() => setAuthModal('login')} 
                currentUserId={user?.id}
              />
            </section>

          </div>
        )}
      </div>

      {/* 4. SCORE SUBMISSION OVERLAY DIALOG REPORT CARD */}
      {completedReport && (
        <div id="report-modal" className="fixed inset-0 bg-slate-950/75 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-3xl max-w-xl w-full p-6 sm:p-8 shadow-2xl border border-slate-800 animate-scale-up text-center max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-4 border border-emerald-500/25">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <h3 className="text-xl font-sans font-black text-slate-100 tracking-tight">Mock Exam Submitted Successfully!</h3>
            <p className="text-[10px] text-amber-500 font-mono font-bold tracking-widest uppercase mt-1">Official Performance Scorecard</p>

            <div className="grid grid-cols-3 gap-3.5 my-8 font-mono text-xs">
              <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-850">
                <p className="text-slate-400 uppercase tracking-wider text-[9px] font-bold">Total Marks</p>
                <p className="text-xl font-sans font-black text-slate-100 mt-1">{completedReport.score} <span className="text-xs text-slate-500">/ {completedReport.totalMarks}</span></p>
              </div>
              <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-850">
                <p className="text-slate-400 uppercase tracking-wider text-[9px] font-bold">Time Invested</p>
                <p className="text-xl font-sans font-black text-slate-100 mt-1">{Math.round(completedReport.attempt.timeSpentSeconds / 60)} <span className="text-xs text-slate-500">Min</span></p>
              </div>
              <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-850">
                <p className="text-slate-400 uppercase tracking-wider text-[9px] font-bold">Accuracy Index</p>
                <p className="text-xl font-sans font-black text-amber-400 mt-1">{Math.round((completedReport.score / completedReport.totalMarks) * 100)}%</p>
              </div>
            </div>

            <p className="text-slate-400 text-xs font-sans max-w-md mx-auto leading-relaxed mb-8">
              Your scorecard has been compiled and cataloged securely. If you belong to an institute, your branch director can audit your comprehensive scorecard and diagnostics now.
            </p>

            <button
              onClick={() => setCompletedReport(null)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-sans font-extrabold text-xs py-3 px-8 rounded-xl shadow-xl shadow-amber-500/10 border border-amber-400/20 transition-all uppercase tracking-wider focus:outline-none"
            >
              Go to Candidate Portal
            </button>
          </div>
        </div>
      )}

      {/* 5. DYNAMIC CORPORATE FOOTER SECTION */}
      {view !== 'exam' && (
        <footer id="corporate-footer" className="bg-slate-950 border-t border-slate-900 py-16 mt-16 text-slate-500 text-xs font-sans">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {/* Column 1: Brand & Logo */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="bg-amber-500 text-slate-950 font-sans font-black text-xs px-2.5 py-0.5 rounded-lg border border-amber-400">लक्ष्य</span>
                <span className="text-sm font-sans font-bold text-slate-200 tracking-tight">NextGen लक्ष्य</span>
              </div>
              <p className="leading-relaxed text-slate-450 text-[11px]">
                India's premiere mock CBT simulator. Replicating the exact design, navigation flow, and analytical scoring rules of national board competitive exams.
              </p>
              <p className="text-[10px] text-slate-600 leading-normal font-mono">
                Powered by HelpingHouse Group
              </p>
            </div>

            {/* Column 2: Platform Pages */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider font-mono">Platform Pages</h4>
              <ul className="space-y-2.5 text-[11px] font-medium">
                <li>
                  <button onClick={() => setView('home')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => setView('blogs')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                    Blogs
                  </button>
                </li>
                <li>
                  <button onClick={() => setFooterModal('about')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => setFooterModal('contact')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                    Contact Us
                  </button>
                </li>
                <li>
                  <button onClick={() => setView('competition')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                    Competition Pages
                  </button>
                </li>
                <li>
                  <button onClick={() => setFooterModal('faq')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none flex items-center gap-1">
                    <span>FAQ</span>
                    <span className="bg-amber-500/10 text-amber-400 text-[9px] font-bold font-mono px-1 rounded">HELP</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 3: Console & Access */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider font-mono">Console & Access</h4>
              <ul className="space-y-2.5 text-[11px] font-medium">
                <li>
                  <button 
                    onClick={() => {
                      if (user && user.role === 'admin') {
                        setView('dashboard');
                      } else {
                        setAuthModal('login');
                        setEmail('admin@nextgen.in');
                        setPassword('');
                        setAuthError('');
                      }
                    }}
                    className="text-slate-450 hover:text-rose-400 transition-colors text-left focus:outline-none flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px]"
                  >
                    <span>Super Admin Console 🔐</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 4: Legal Pages */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider font-mono">Legal Pages</h4>
              <ul className="space-y-2.5 text-[11px] font-medium">
                {legalPages && legalPages.length > 0 ? (
                  legalPages.map(page => (
                    <li key={page.id}>
                      <button onClick={() => setFooterModal(page.slug)} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none text-left">
                        {page.title}
                      </button>
                    </li>
                  ))
                ) : (
                  <>
                    <li>
                      <button onClick={() => setFooterModal('privacy')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                        Privacy Policy
                      </button>
                    </li>
                    <li>
                      <button onClick={() => setFooterModal('terms')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                        Terms & Conditions
                      </button>
                    </li>
                    <li>
                      <button onClick={() => setFooterModal('refund')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                        Refund Policy
                      </button>
                    </li>
                    <li>
                      <button onClick={() => setFooterModal('disclaimer')} className="text-slate-450 hover:text-amber-400 transition-colors focus:outline-none">
                        Disclaimer
                      </button>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Column 5: Weekly Bulletins & Mock Tips Alerts */}
            <div className="space-y-4 col-span-1">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider font-mono">Study Tips & Alerts</h4>
              <p className="leading-relaxed text-slate-450 text-[11px]">
                Subscribe for weekly topper secrets, upcoming exam alerts, and mnemonic mock hack bulletins delivered straight to your inbox.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-1.5">
                  <input 
                    type="email" 
                    placeholder="Enter student email..." 
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={newsletterStatus === 'loading'}
                    required
                    className="flex-1 bg-slate-900 border border-slate-800 rounded-lg py-2 px-3 text-slate-200 placeholder-slate-650 focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:opacity-50 text-[11px]"
                  />
                  <button 
                    type="submit" 
                    disabled={newsletterStatus === 'loading'}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-[10px] px-3.5 py-2 rounded-lg transition-all focus:outline-none disabled:opacity-50 uppercase tracking-wider text-center"
                  >
                    {newsletterStatus === 'loading' ? 'Submitting...' : 'Sign Up'}
                  </button>
                </div>
                {newsletterMsg && (
                  <p className={`text-[10px] font-mono font-medium ${newsletterStatus === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {newsletterMsg}
                  </p>
                )}
              </form>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-900/60 mt-12 pt-6 text-center text-[10px] text-slate-600 font-mono tracking-wider">
            © 2026 NEXTGEN लक्ष्य. All Rights Reserved. | Co-Powered by HHGVerify.in & HHGAcademica.in
          </div>
        </footer>
      )}

      {/* 6. MODAL DRAWER OVERLAYS FOR AUTH (REGISTER / LOGIN SHEET) */}
      {authModal && (
        <div id="auth-overlay-drawer" className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-800 animate-scale-up relative max-h-[90vh] overflow-y-auto scrollbar-thin">
            <button 
              onClick={() => {
                setAuthModal(null);
                setAuthError('');
              }}
              className="absolute right-4.5 top-4.5 p-1.5 text-slate-450 hover:text-slate-100 rounded-lg bg-slate-950/40 border border-slate-850 hover:bg-slate-950 transition-colors focus:outline-none"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center mb-6">
              <h3 className="text-xl font-sans font-black text-slate-100 tracking-tight uppercase">
                {authModal === 'login' ? 'Candidate Log In' : 'Candidate Registration'}
              </h3>
              <p className="text-[9px] text-amber-500 font-mono uppercase tracking-widest mt-1 font-bold">
                {authModal === 'login' ? 'Access your exam lockers' : 'Create secure prep profile'}
              </p>
            </div>

            {authError && (
              <div id="auth-error-box" className="mb-4 p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-semibold flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            <form onSubmit={handleAuthSubmit} className="space-y-4 text-xs font-sans">
              
              {/* Registration Specific Fields */}
              {authModal === 'signup' && (
                <>
                  <div>
                    <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">Full Name</label>
                    <input 
                      type="text" 
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Rahul Sharma" 
                      className="w-full bg-slate-950 border border-slate-850 text-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500/50 text-xs font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">Phone Number</label>
                    <input 
                      type="text" 
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="e.g. +91 98765 43210" 
                      className="w-full bg-slate-950 border border-slate-850 text-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500/50 text-xs font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">Institute Association (Optional)</label>
                    <select 
                      value={selectedInstId}
                      onChange={(e) => setSelectedInstId(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-850 text-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500/50 text-xs font-medium"
                    >
                      <option value="">None (Independent Student)</option>
                      {institutes.map((inst) => (
                        <option key={inst.id} value={inst.id}>{inst.name} ({inst.branch})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">Referral Code (Optional)</label>
                    <input 
                      type="text" 
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value)}
                      placeholder="e.g. ANJALI66" 
                      className="w-full bg-slate-950 border border-slate-850 text-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500/50 text-xs font-mono tracking-wider"
                    />
                  </div>
                </>
              )}

              {authModal === 'login' && (
                <div className="grid grid-cols-2 gap-2 mb-4 bg-slate-950/40 p-1 rounded-xl border border-slate-850">
                  <button
                    type="button"
                    onClick={() => {
                      setLoginRole('student');
                      setAuthError('');
                    }}
                    className={`py-2 text-center rounded-lg font-bold transition-all ${
                      loginRole === 'student'
                        ? 'bg-amber-500 text-slate-950 shadow'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Student Login
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginRole('institute');
                      setAuthError('');
                    }}
                    className={`py-2 text-center rounded-lg font-bold transition-all ${
                      loginRole === 'institute'
                        ? 'bg-amber-500 text-slate-950 shadow'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Institute Login
                  </button>
                </div>
              )}

              <div>
                <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">Email Address</label>
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@email.com" 
                  className="w-full bg-slate-950 border border-slate-850 text-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500/50 text-xs font-medium"
                />
              </div>

              <div>
                <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">Secure Password</label>
                <input 
                  type="password" 
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" 
                  className="w-full bg-slate-950 border border-slate-850 text-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500/50 text-xs font-medium"
                />
              </div>

              <button 
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-950 font-sans font-black text-xs rounded-xl shadow-lg transition-all focus:outline-none uppercase tracking-wider mt-4"
              >
                {loading ? 'Authenticating...' : authModal === 'login' ? `Sign In as ${loginRole === 'student' ? 'Student' : 'Institute'}` : 'Complete Registration'}
              </button>

              <div className="text-center pt-3 text-slate-450 text-[11px] font-sans font-medium">
                {authModal === 'login' ? (
                  loginRole === 'student' ? (
                    email.toLowerCase() !== 'admin@nextgen.in' ? (
                      <p>
                        New candidate on NextGen?{' '}
                        <button 
                          type="button"
                          onClick={() => {
                            setAuthModal('signup');
                            setAuthError('');
                          }}
                          className="text-amber-400 font-bold hover:underline focus:outline-none"
                        >
                          Create secure profile
                        </button>
                      </p>
                    ) : null
                  ) : (
                    <p className="text-slate-500 italic">
                      * Institute registration is restricted. Only the Super Admin can register new institutes.
                    </p>
                  )
                ) : (
                  <p>
                    Already have an exam profile?{' '}
                    <button 
                      type="button"
                      onClick={() => {
                        setAuthModal('login');
                        setAuthError('');
                      }}
                      className="text-amber-400 font-bold hover:underline focus:outline-none"
                    >
                      Sign In here
                    </button>
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. OTP VERIFICATION SHEET */}
      {otpVerificationEmail && (
        <div id="otp-verification-modal" className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-800 animate-scale-up relative max-h-[90vh] overflow-y-auto scrollbar-thin">
            <button 
              onClick={() => {
                setOtpVerificationEmail(null);
                setOtpDemoHint(null);
                setOtpCodeInput('');
              }}
              className="absolute right-4.5 top-4.5 p-1.5 text-slate-450 hover:text-slate-100 rounded-lg bg-slate-950/40 border border-slate-850 hover:bg-slate-950 transition-colors focus:outline-none"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3 border border-amber-500/20">
                <ShieldCheck className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-sans font-black text-slate-100 tracking-tight uppercase">
                OTP Verification Required
              </h3>
              <p className="text-[10px] text-slate-400 font-sans mt-1">
                A verification code has been dispatched to <strong>{otpVerificationEmail}</strong>. Please verify to activate your candidate portal.
              </p>
            </div>

            {authError && (
              <div className="mb-4 p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-semibold flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            <form onSubmit={handleOtpVerifySubmit} className="space-y-4 text-xs font-sans">
              <div>
                <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1 text-center">Enter 6-Digit OTP</label>
                <input 
                  type="text" 
                  maxLength={6}
                  required
                  value={otpCodeInput}
                  onChange={(e) => setOtpCodeInput(e.target.value)}
                  placeholder="e.g. 123456" 
                  className="w-full bg-slate-950 border border-slate-850 text-slate-150 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500/50 text-center text-lg font-mono tracking-widest"
                />
              </div>

              <button 
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-950 font-sans font-black text-xs rounded-xl shadow-lg transition-all focus:outline-none uppercase tracking-wider"
              >
                {loading ? 'Verifying OTP...' : 'Verify & Activate Account'}
              </button>

              <div className="text-center pt-3 text-slate-450 text-[11px] font-sans font-medium flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading}
                  className="text-amber-500 font-bold hover:underline focus:outline-none disabled:opacity-55"
                >
                  Resend Verification OTP
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOtpVerificationEmail(null);
                    setAuthModal('login');
                  }}
                  className="text-slate-450 hover:text-slate-200 font-bold focus:outline-none"
                >
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 8. FORCE SUPER ADMIN PASSWORD CHANGE OVERLAY */}
      {user && (user as any).mustChangePassword && (
        <div id="force-password-change-modal" className="fixed inset-0 bg-slate-950/95 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-rose-500/30 animate-scale-up relative max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-3 border border-rose-500/20">
                <ShieldAlert className="w-6 h-6 text-rose-500 animate-pulse" />
              </div>
              <h3 className="text-lg font-sans font-black text-slate-100 tracking-tight uppercase">
                Critical Security Policy Action
              </h3>
              <p className="text-[10px] text-amber-500 font-mono uppercase tracking-widest mt-1 font-bold">
                Default Password Detected
              </p>
              <p className="text-[11px] text-slate-400 font-sans mt-2 leading-relaxed">
                As part of NextGen Security Guardrails, the default password for <strong className="text-slate-200">admin@nextgen.in</strong> must be changed before accessing any super admin components.
              </p>
            </div>

            {forcePassError && (
              <div id="force-password-error" className="mb-4 p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-semibold flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                <span>{forcePassError}</span>
              </div>
            )}

            <form onSubmit={handleForcePasswordChangeSubmit} className="space-y-4 text-xs font-sans">
              <div>
                <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">New Password (Min 8 chars)</label>
                <input 
                  type="password" 
                  required
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  placeholder="Enter new strong password" 
                  className="w-full bg-slate-950 border border-slate-850 text-slate-250 px-4 py-3 rounded-xl focus:outline-none focus:border-rose-500/50 text-xs font-medium"
                />
              </div>

              <div>
                <label className="block text-slate-400 font-mono font-bold uppercase tracking-wide mb-1">Confirm New Password</label>
                <input 
                  type="password" 
                  required
                  value={confirmAdminPassword}
                  onChange={(e) => setConfirmAdminPassword(e.target.value)}
                  placeholder="Re-enter password to verify" 
                  className="w-full bg-slate-950 border border-slate-850 text-slate-250 px-4 py-3 rounded-xl focus:outline-none focus:border-rose-500/50 text-xs font-medium"
                />
              </div>

              <button 
                type="submit"
                disabled={forcePassLoading}
                className="w-full py-3 bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-500 hover:to-amber-500 disabled:opacity-50 text-white font-sans font-black text-xs rounded-xl shadow-lg transition-all focus:outline-none uppercase tracking-wider mt-4 border border-rose-400/20"
              >
                {forcePassLoading ? 'Securing Profile...' : 'Update Password & Authorize'}
              </button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-slate-500 hover:text-slate-350 font-bold text-[10px] uppercase tracking-wider underline focus:outline-none"
                >
                  Cancel & Logout Session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. DYNAMIC FOOTER PAGE OVERLAY MODALS */}
      {footerModal && (
        <div id="footer-modal-overlay" className="fixed inset-0 bg-slate-950/85 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6 transition-all animate-fade-in">
          <div className={`bg-slate-900 border border-slate-800/85 rounded-2xl w-full p-6 sm:p-8 shadow-2xl relative space-y-6 text-slate-300 ${(footerModal === 'faq' || (legalPages && legalPages.some(page => page.slug === footerModal))) ? 'max-w-3xl' : 'max-w-lg'}`}>
            <button 
              onClick={() => setFooterModal(null)}
              className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors focus:outline-none"
              title="Close Panel"
            >
              <X className="w-5 h-5" />
            </button>

            {footerModal === 'about' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2.5 pb-3 border-b border-slate-850">
                  <span className="bg-amber-500 text-slate-950 font-sans font-black text-xs px-2.5 py-0.5 rounded-lg border border-amber-400">लक्ष्य</span>
                  <h3 className="text-lg font-sans font-black text-slate-100 tracking-tight">About NextGen लक्ष्य</h3>
                </div>
                <p className="text-xs leading-relaxed font-sans text-slate-400">
                  Welcome to <strong>NextGen लक्ष्य</strong>, India's next-generation portal designed to accelerate candidate exam-readiness through advanced simulated computer-based tests.
                </p>
                <p className="text-xs leading-relaxed font-sans text-slate-400">
                  Equipped with real-time analytics dashboards, automated referral bonus programs, secure multi-branch tenant isolation, and detailed test performance metrics, we help aspirants prepare with zero anxiety.
                </p>
                <p className="text-[10px] text-slate-500 font-mono pt-2 border-t border-slate-850">
                  Powered & Maintained by HelpingHouse Group (HHG).
                </p>
              </div>
            )}

            {footerModal === 'contact' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-850">
                  <Mail className="w-5 h-5 text-amber-500" />
                  <h3 className="text-lg font-sans font-black text-slate-100 tracking-tight">Contact Us</h3>
                </div>
                <p className="text-xs leading-relaxed font-sans text-slate-400">
                  Reach out to the NextGen Support Desk for any queries regarding registration, referral points verification, or mock keys activation.
                </p>
                <div id="footer-modal-contact-details" className="space-y-3 bg-gradient-to-b from-slate-950/60 to-slate-950/30 border border-slate-800/80 rounded-2xl p-4 font-sans mt-2 shadow-xl backdrop-blur-sm">
                  <div className="flex items-center gap-3.5 text-xs p-3 rounded-xl bg-slate-900/40 hover:bg-slate-900/80 border border-slate-850/60 hover:border-amber-500/30 transition-all duration-300 shadow-sm hover:shadow-md hover:shadow-amber-500/[0.02]">
                    <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-500 flex-shrink-0">
                      <Phone className="w-4 h-4 animate-pulse" />
                    </div>
                    <div>
                      <span className="text-[9px] text-slate-500 block uppercase font-mono tracking-wider font-semibold">Phone Helpline</span>
                      <span className="text-slate-200 font-bold font-sans">{cms?.contactPhone || '+91 98765 43210'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3.5 text-xs p-3 rounded-xl bg-slate-900/40 hover:bg-slate-900/80 border border-slate-850/60 hover:border-amber-500/30 transition-all duration-300 shadow-sm hover:shadow-md hover:shadow-amber-500/[0.02]">
                    <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-500 flex-shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[9px] text-slate-500 block uppercase font-mono tracking-wider font-semibold">Email Support</span>
                      <span className="text-slate-200 font-bold font-sans">{cms?.contactEmail || 'support@nextgenlakshya.in'}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3.5 text-xs p-3 rounded-xl bg-slate-900/40 hover:bg-slate-900/80 border border-slate-850/60 hover:border-amber-500/30 transition-all duration-300 shadow-sm hover:shadow-md hover:shadow-amber-500/[0.02]">
                    <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-500 flex-shrink-0 mt-0.5">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[9px] text-slate-500 block uppercase font-mono tracking-wider font-semibold">Corporate Address</span>
                      <span className="text-slate-200 font-bold font-sans leading-relaxed">{cms?.contactAddress || 'Laxmi Tower, BKC, Mumbai 400051'}</span>
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 font-mono pt-2 border-t border-slate-850">
                  Co-Powered by HHGVerify.in & HHGAcademica.in
                </p>
              </div>
            )}

            {footerModal && legalPages && legalPages.some(page => page.slug === footerModal) && (() => {
              const page = legalPages.find(p => p.slug === footerModal);
              if (!page) return null;
              return (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-3 border-b border-slate-850">
                    <ShieldCheck className="w-5.5 h-5.5 text-emerald-450" />
                    <h3 className="text-xl font-sans font-black text-slate-100 tracking-tight">{page.title}</h3>
                  </div>
                  <div className="max-h-[55vh] overflow-y-auto pr-2 custom-scrollbar">
                    <RichContentRenderer content={page.content} />
                  </div>
                  <p className="text-[10px] text-slate-500 font-mono pt-2.5 border-t border-slate-850 mt-2">
                    Last updated: {page.lastUpdated || '2026-07-16'} | Compliant with HelpingHouse National Cyber Security guidelines.
                  </p>
                </div>
              );
            })()}

            {footerModal === 'faq' && (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                <div className="flex items-center gap-2.5 pb-3 border-b border-slate-850 sticky top-0 bg-slate-900 z-10">
                  <BookOpen className="w-5 h-5 text-amber-500" />
                  <h3 className="text-lg font-sans font-black text-slate-100 tracking-tight">Frequently Asked Questions</h3>
                </div>

                <div id="footer-modal-faq-accordion" className="space-y-4 pt-1">
                  <div className="text-[10px] font-black text-amber-400 font-mono uppercase tracking-widest flex items-center gap-1.5 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                    <span>Test Formats & CBT Engine</span>
                  </div>
                  
                  <details className="group border border-slate-800/60 hover:border-slate-700/80 rounded-xl bg-gradient-to-b from-slate-950/40 to-slate-950/15 p-4 transition-all duration-300 shadow-sm hover:shadow-md">
                    <summary className="flex items-center justify-between cursor-pointer focus:outline-none list-none [&::-webkit-details-marker]:hidden select-none">
                      <h4 className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors pr-4">What exam formats are simulated on NextGen लक्ष्य?</h4>
                      <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-300 group-open:rotate-180 group-hover:text-slate-300" />
                    </summary>
                    <p className="mt-3 pt-3 border-t border-slate-850/80 text-xs text-slate-400 leading-relaxed font-sans">
                      We host hyper-realistic CBT (Computer Based Test) simulations for major national exams including **CAT, JEE, NEET, and Banking**. Our mock tests mirror official weightage, marking metrics (such as +4/-1 schemes with detailed subject categorization), and user interfaces to help you build actual test-day confidence and stamina.
                    </p>
                  </details>

                  <details className="group border border-slate-800/60 hover:border-slate-700/80 rounded-xl bg-gradient-to-b from-slate-950/40 to-slate-950/15 p-4 transition-all duration-300 shadow-sm hover:shadow-md">
                    <summary className="flex items-center justify-between cursor-pointer focus:outline-none list-none [&::-webkit-details-marker]:hidden select-none">
                      <h4 className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors pr-4">Can I pause my exam and resume it later?</h4>
                      <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-300 group-open:rotate-180 group-hover:text-slate-300" />
                    </summary>
                    <p className="mt-3 pt-3 border-t border-slate-850/80 text-xs text-slate-400 leading-relaxed font-sans">
                      Yes! Your progress is backed up in real-time. If you close your browser or pause the test, you can click **"Resume Exam"** directly on your Student Dashboard to pick up exactly where you left off.
                    </p>
                  </details>

                  <div className="text-[10px] font-black text-amber-400 font-mono uppercase tracking-widest flex items-center gap-1.5 pt-3.5 pb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                    <span>Subscriptions & Premium Plans</span>
                  </div>

                  <details className="group border border-slate-800/60 hover:border-slate-700/80 rounded-xl bg-gradient-to-b from-slate-950/40 to-slate-950/15 p-4 transition-all duration-300 shadow-sm hover:shadow-md">
                    <summary className="flex items-center justify-between cursor-pointer focus:outline-none list-none [&::-webkit-details-marker]:hidden select-none">
                      <h4 className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors pr-4">How do I unlock Premium Mock Exams?</h4>
                      <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-300 group-open:rotate-180 group-hover:text-slate-300" />
                    </summary>
                    <p className="mt-3 pt-3 border-t border-slate-850/80 text-xs text-slate-400 leading-relaxed font-sans">
                      Premium exams are marked with a 🔒 lock icon. You can unlock them by upgrading your student tier through our active Subscription Plans. Alternatively, you can redeem your accumulated Referral Points to buy or extend subscription keys.
                    </p>
                  </details>

                  <details className="group border border-slate-800/60 hover:border-slate-700/80 rounded-xl bg-gradient-to-b from-slate-950/40 to-slate-950/15 p-4 transition-all duration-300 shadow-sm hover:shadow-md">
                    <summary className="flex items-center justify-between cursor-pointer focus:outline-none list-none [&::-webkit-details-marker]:hidden select-none">
                      <h4 className="text-xs font-bold text-slate-200 group-hover:text-amber-450 transition-colors pr-4">What is the refund policy on subscription plans?</h4>
                      <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-300 group-open:rotate-180 group-hover:text-slate-300" />
                    </summary>
                    <p className="mt-3 pt-3 border-t border-slate-850/80 text-xs text-slate-400 leading-relaxed font-sans">
                      Since practice questions and exam keys can be instantly accessed or solved upon subscription, all purchases are non-refundable. For detailed conditions, please read our Refund Policy modal.
                    </p>
                  </details>

                  <div className="text-[10px] font-black text-amber-400 font-mono uppercase tracking-widest flex items-center gap-1.5 pt-3.5 pb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                    <span>Referral Program & Points</span>
                  </div>

                  <details className="group border border-slate-800/60 hover:border-slate-700/80 rounded-xl bg-gradient-to-b from-slate-950/40 to-slate-950/15 p-4 transition-all duration-300 shadow-sm hover:shadow-md">
                    <summary className="flex items-center justify-between cursor-pointer focus:outline-none list-none [&::-webkit-details-marker]:hidden select-none">
                      <h4 className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors pr-4">How do I earn referral points?</h4>
                      <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-300 group-open:rotate-180 group-hover:text-slate-300" />
                    </summary>
                    <p className="mt-3 pt-3 border-t border-slate-850/80 text-xs text-slate-400 leading-relaxed font-sans">
                      Every registered student has a unique referral code. You earn bonus points (₹10 equivalent per signup) when friends sign up using your code, and premium upgrade bonus points (₹50 equivalent) when they upgrade to any of our Premium subscription plans.
                    </p>
                  </details>

                  <details className="group border border-slate-800/60 hover:border-slate-700/80 rounded-xl bg-gradient-to-b from-slate-950/40 to-slate-950/15 p-4 transition-all duration-300 shadow-sm hover:shadow-md">
                    <summary className="flex items-center justify-between cursor-pointer focus:outline-none list-none [&::-webkit-details-marker]:hidden select-none">
                      <h4 className="text-xs font-bold text-slate-200 group-hover:text-amber-400 transition-colors pr-4">How do I request a wallet payout or redeem points?</h4>
                      <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-300 group-open:rotate-180 group-hover:text-slate-300" />
                    </summary>
                    <p className="mt-3 pt-3 border-t border-slate-850/80 text-xs text-slate-400 leading-relaxed font-sans">
                      Navigate to the **Scores & Diagnostics** tab in your Student Panel. If your referral points balance meets or exceeds the minimum payout threshold (typically ₹10), you can enter your payout address/details and submit a request directly to the admin queue.
                    </p>
                  </details>
                </div>

                <p className="text-[10px] text-slate-500 font-mono pt-2 border-t border-slate-850">
                  Have more questions? Reach out via the Contact Us form in the footer or email us at support@nextgenlakshya.in.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setFooterModal(null)}
                className="px-6 py-2.5 bg-slate-850 hover:bg-slate-800 text-slate-100 font-sans font-bold text-xs rounded-xl transition-all border border-slate-800 focus:outline-none uppercase tracking-wider"
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </HelmetProvider>
  );
}
